import { NavigationOptions, RowOptions } from 'interfaces';
import authUtils from './auth';
import { itemIsNullOrUndefined } from './common';
import RolesEnum from '@/const/role';
import { Permission } from '@/lib/permissions';

/**
 * Filter a set of options based on the user roles and permissions
 *
 * The options will be filtered based on the permissions/roles that are inside options prop
 * and the current user (session user) permissions/roles
 *
 * This function specifically works with Navigation Options
 * @param baseOptions Initial options to be filtered
 * @returns Filtered options
 */
export function filterNavigationOptionsByRolesOrPermissions(
	baseOptions: NavigationOptions[],
	pathname?: string,
	permissions: Permission[] = []
): NavigationOptions[] {
	return baseOptions
		.filter((option: NavigationOptions) => {
			// Si no requiere roles/permissions, se incluye
			if (
				(itemIsNullOrUndefined(option.roles) || option.roles?.length === 0) &&
				(itemIsNullOrUndefined(option.permissions) ||
					option.permissions?.length === 0)
			) {
				return true;
			}

			// Calcula permisos/roles
			const hasAnyRole = checkRoleLocally(option.roles, pathname);

			// Si tienes una función local para comparar permisos, úsala.
			// Aquí asumo que `Permission` es comparable con `includes`.
			const hasAnyPermission =
				option.permissions?.some((p) => (permissions as any[]).includes(p)) ??
				false;

			return hasAnyRole || hasAnyPermission;
		})
		.map((option: NavigationOptions) => {
			// Clonar para no mutar la referencia original
			const next: NavigationOptions = { ...option };

			// Controlar subOptions
			if (next.subOptions && next.subOptions.length > 0) {
				const canCreateCases = allowCreateCases(permissions);
				const showSubOptions = next.name === 'cases' ? canCreateCases : true;

				next.subOptions = showSubOptions
					? filterNavigationOptionsByRolesOrPermissions(
							next.subOptions,
							pathname,
							permissions
					  )
					: undefined; // <— si es 'cases' y NO puede crear, eliminamos subOptions
			}

			return next;
		});
}

const checkRoleLocally = (
	roles: RolesEnum[] | undefined,
	pathname: string | undefined
) => {
	const currentRole = pathname?.startsWith('/clientapp')
		? RolesEnum.CLIENT
		: RolesEnum.LAWYER;
	return roles?.includes(currentRole);
};

const allowCreateCases = (permissions: Permission[]): boolean => {
	if (permissions.length > 0) {
		return (
			permissions.find((p) => p.value === 'case_admin')?.is_allowed ?? false
		);
	}

	return false;
};

/**
 * Filter a set of options based on the user roles and permissions
 *
 * The options will be filtered based on the permissions/roles that are inside options prop
 * and the current user (session user) permissions/roles
 *
 * This function works specifically with Row Options in the tables
 *
 * @param baseOptions Initial options to be filtered
 * @returns Filtered options
 */
export function filterRowOptionsByRolesOrPermissions<T>(
	baseOptions: RowOptions<T>[]
): RowOptions<T>[] {
	return baseOptions.filter((option) => {
		// If the option has no roles or permissions specified, include it in the filtered array
		if (
			(itemIsNullOrUndefined(option.roles) || option.roles?.length === 0) &&
			(itemIsNullOrUndefined(option.permissions) ||
				option.permissions?.length === 0)
		) {
			return true;
		}

		// Check if user has permissions/roles to see this item
		const hasAnyRole = option.roles && authUtils.hasAnyRole(option.roles);
		const hasAnyPermission =
			option.permissions && authUtils.hasAnyPermission(option.permissions);

		return hasAnyRole || hasAnyPermission;
	});
}
